/**
 * This file contiains the new price form for the general payment system.
 */
import { showToast } from '@components/toaster';
import { AsyncForm, FormGroup, FormSection, FormSubSection } from '@components/async-form';
import { BtnPrimary, BtnSecondary, Button } from '@components/buttons';
import { useState } from 'preact/hooks';
import {
  IcoCalendar,
  IcoChevronDown,
  IcoChevronLeft,
  IcoCreditCard,
  IcoDollar,
  IcoGift,
  IcoHeart,
  IcoRefresh,
} from '@components/icons';
import { formDataToPriceLimits, SignupLimitsFormFields } from './signup-limits-tab';
import { HeadingPrimary } from '@course/components/headings';
import { Currency, PaymentType, PriceRow } from 'server/types';
import { CurrencyInput } from './currency-input';
import { decimalToCents } from 'shared/payments/fmt';
import { BtnStripeConnect } from './stripe-connect';
import { completeCourseStep } from '@course/components/course-checklist';
import { Toggle } from '@components/toggle';
import { AccessLimitsFormFields } from './access-limits-tab';
import { useRouteParams, useRouter } from '@components/router';
import { isNullish } from 'shared/utils';
import type { h } from 'preact';

interface Props {
  courseId?: string;
  cancelHref?: string;
  onCancel?(): void;
  supportsStripe: boolean;
  createPrice(
    p: Omit<PriceRow, 'id' | 'productId' | 'createdAt' | 'updatedAt'>,
  ): Promise<{ id: string }>;
}

type UIPaymentType = PaymentType | 'pwyc';

function uiTypeToServerType(uiType: UIPaymentType): PaymentType {
  if (uiType === 'pwyc') {
    return 'paid';
  }
  return uiType;
}

export async function onSubmit(
  props: Pick<Props, 'createPrice' | 'supportsStripe'>,
  formData: any,
) {
  const { createPrice, supportsStripe } = props;
  const limits = formDataToPriceLimits(formData);
  const currency: Currency = formData.currency || 'USD';
  const uiType: UIPaymentType = formData.paymentType;
  const paymentType = uiTypeToServerType(uiType);
  const freeTrialPeriod = (formData.freeTrialPeriod && parseInt(formData.freeTrialPeriod, 10)) || 0;
  const payWhatYouCan = uiType === 'pwyc' || (paymentType === 'paid' && !!formData.payWhatYouCan);
  const price = await createPrice({
    paymentType,
    isEnabled: true,
    name: formData.name || nameFromUIType(uiType),
    currency,
    interval: formData.interval,
    allowStripe: supportsStripe && paymentType !== 'free' && formData.allowStripe !== false,
    freeTrialPeriod,
    isGiftable: formData.isGiftable,
    allowSelfCancel: !formData.requireContactToCancel,
    numPayments: formData.numPayments ? parseInt(formData.numPayments) : undefined,
    priceInCents: decimalToCents(formData.priceInCents, currency),
    payWhatYouCan,
    minPriceInCents: payWhatYouCan ? decimalToCents(formData.minPriceInCents, currency) : 0,
    accessDuration: formData.accessDuration ? parseInt(formData.accessDuration) : undefined,
    expiresOn: limits.expiresOn || undefined,
    availableOn: limits.availableOn || undefined,
    maxPurchases: isNullish(limits.maxPurchases) ? undefined : limits.maxPurchases,
    redirectUrl: formData.redirectUrl || undefined,
  });
  showToast({
    type: 'ok',
    title: 'Saved',
    message: 'Price point saved.',
  });
  return price;
}

export function PriceFormFields({
  courseId,
  supportsStripe,
}: Pick<Props, 'courseId' | 'supportsStripe'>) {
  const [uiType, setUIType] = useState<UIPaymentType>('free');
  const paymentType = uiTypeToServerType(uiType);

  // The div below is important. We were using Fragment <></> and that actually
  // caused weird reflow issues with Preact. We may try upgrading Preact to
  // see if that is fixed.
  return (
    <div class="space-y-10">
      <ChoosePaymentType
        courseId={courseId}
        uiType={uiType}
        setUIType={setUIType}
        supportsStripe={!!supportsStripe}
      />
      <Price uiType={uiType} />
      <PricePointName paymentType={paymentType} uiType={uiType} />
      <div class="border rounded-lg p-8">
        <h2 class="text-xl mb-6">Options</h2>
        <div class="flex flex-col gap-10">
          <Provider paymentType={paymentType} supportsStripe={supportsStripe} />
          <FreeTrialPeriod paymentType={paymentType} />
          {courseId && <RequireContactToCancel paymentType={paymentType} defaultValue={true} />}
          {courseId && <AllowGiftsToggle paymentType={paymentType} defaultValue={true} />}
          {courseId && <AccessLimitsFormFields accessDuration={-1} />}
          <RedirectUrlField />
          <ExpandableLimitsFields />
        </div>
      </div>
    </div>
  );
}

export function NewPriceForm(props: Props) {
  const { courseId, cancelHref, onCancel } = props;
  const router = useRouter();
  return (
    <AsyncForm
      class="flex flex-col"
      onSubmit={async (formData) => {
        await onSubmit(props, formData);
        if (courseId) {
          completeCourseStep(router, 'pricePoint');
        }
      }}
    >
      {cancelHref && <BackNavigation cancelHref={cancelHref} />}
      <HeadingPrimary
        title={
          <span class="flex items-center">
            <IcoCreditCard class="w-12 h-12 bg-green-400 rounded-full text-white p-2 mr-4" />
            New Price Point
          </span>
        }
      />
      <PriceFormFields {...props} />
      <footer class="flex items-center mt-10">
        <BtnPrimary class="mr-3">Create Price Point</BtnPrimary>
        <BtnSecondary type="button" href={cancelHref} onClick={onCancel}>
          Cancel
        </BtnSecondary>
      </footer>
    </AsyncForm>
  );
}

function BackNavigation({ cancelHref }: Pick<Props, 'cancelHref'>) {
  return (
    <header class="mb-2">
      <a href={cancelHref} class="inline-flex items-center space-x-1.5 text-zinc-400 -ml-1">
        <IcoChevronLeft />
        <span>Back to price points</span>
      </a>
    </header>
  );
}

export function PayWhatYouCanToggle({
  paymentType,
  defaultChecked,
  defaultMinPrice,
  defaultSuggestedPrice,
  showSuggestedPrice,
}: {
  paymentType: PaymentType;
  defaultChecked?: boolean;
  defaultMinPrice?: number;
  defaultSuggestedPrice?: number;
  showSuggestedPrice?: boolean;
}) {
  const [enabled, setEnabled] = useState(!!defaultChecked);

  if (paymentType !== 'paid') {
    return null;
  }

  return (
    <div class="flex flex-col gap-4">
      <label class="inline-flex items-center gap-3">
        <Toggle checked={enabled} onClick={() => setEnabled(!enabled)} name="payWhatYouCan" />
        <span>Pay what you can pricing</span>
      </label>
      {enabled && (
        <div class="ml-12 flex flex-col gap-3">
          {showSuggestedPrice ? (
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-700">Suggested amount</span>
              <input
                type="number"
                step="0.01"
                min="0"
                name="suggestedPrice"
                placeholder="0"
                value={defaultSuggestedPrice ? (defaultSuggestedPrice / 100).toString() : ''}
                class="inline-ruz-input w-28"
              />
              <span class="text-xs text-gray-500">
                The slider defaults to this amount. Students can adjust up or down.
              </span>
            </label>
          ) : (
            <span class="text-sm text-gray-600">
              The price above becomes the suggested amount. Students can enter a custom amount.
            </span>
          )}
          <label class="inline-flex items-center gap-2 text-sm">
            <span>Minimum price:</span>
            <input
              type="number"
              step="0.01"
              min="0"
              name="minPriceInCents"
              placeholder="0"
              value={defaultMinPrice ? (defaultMinPrice / 100).toString() : ''}
              class="inline-ruz-input w-28"
            />
          </label>
        </div>
      )}
    </div>
  );
}

function Price({ uiType }: { uiType: UIPaymentType }) {
  if (uiType === 'pwyc') {
    return (
      <FormSection title="Pricing">
        <FormSubSection>
          <FormGroup prop="priceInCents">
            <div class="flex flex-col gap-4">
              <label class="flex flex-col gap-1">
                <span class="text-sm font-medium text-gray-700">Suggested amount</span>
                <CurrencyInput name="priceInCents" />
              </label>
              <label class="flex flex-col gap-1">
                <span class="text-sm font-medium text-gray-700">Minimum amount</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="minPriceInCents"
                  placeholder="0"
                  class="inline-ruz-input w-28"
                />
                <span class="text-xs text-gray-500">
                  Leave at 0 to allow free access. Students choose what to pay.
                </span>
              </label>
            </div>
          </FormGroup>
        </FormSubSection>
      </FormSection>
    );
  }
  if (uiType === 'paid') {
    return (
      <FormSection title="Price">
        <FormSubSection>
          <FormGroup prop="priceInCents">
            <CurrencyInput name="priceInCents" />
          </FormGroup>
        </FormSubSection>
      </FormSection>
    );
  }
  if (uiType === 'paymentplan') {
    return (
      <FormSection title="Price">
        <FormSubSection>
          <FormGroup prop="priceInCents">
            <FormGroup prop="numPayments">
              <CurrencyInput name="priceInCents" />
              <span class="mx-2">every month for</span>
              <input
                type="number"
                name="numPayments"
                min="1"
                max="50"
                class="inline-ruz-input w-16 appearance-none"
                required
                placeholder="#"
              />
              <span class="mx-2">months</span>
            </FormGroup>
          </FormGroup>
        </FormSubSection>
      </FormSection>
    );
  }
  if (uiType === 'subscription') {
    return (
      <FormSection title="Configure subscription">
        <FormSubSection>
          <FormGroup prop="priceInCents">
            <CurrencyInput name="priceInCents" />
            <span class="mx-2">every</span>
            <select name="interval" class="inline-ruz-input">
              <option value="month">month</option>
              <option value="year">year</option>
            </select>
          </FormGroup>
        </FormSubSection>
        <div class="text-gray-500 mt-4">Subscriptions are handled through Stripe.</div>
      </FormSection>
    );
  }
  return null;
}

export function AllowGiftsToggle({
  paymentType,
  defaultValue,
}: {
  paymentType: PaymentType;
  defaultValue: boolean;
}) {
  const { courseId } = useRouteParams();

  if (paymentType !== 'paid' || !courseId) {
    return null;
  }

  return (
    <label class="inline-flex items-center space-x-3">
      <Toggle defaultChecked={defaultValue} name="isGiftable" />
      <span>Allow gift purchases</span>
    </label>
  );
}

export function RequireContactToCancel({
  paymentType,
  defaultValue,
}: {
  paymentType: PaymentType;
  defaultValue: boolean;
}) {
  const { courseId } = useRouteParams();

  if (paymentType !== 'paymentplan' || !courseId) {
    return null;
  }

  return (
    <FormSection title="Cancellation policy">
      <FormSubSection>
        <label class="inline-flex items-center space-x-3">
          <Toggle defaultChecked={defaultValue} name="requireContactToCancel" />
          <span>Require students to contact you in order to cancel their payment plan</span>
        </label>
      </FormSubSection>
    </FormSection>
  );
}

export function FreeTrialPeriod(props: { paymentType: PaymentType; value?: number }) {
  const { paymentType } = props;
  const [value, setValue] = useState(props.value || 0);

  if (paymentType !== 'paymentplan' && paymentType !== 'subscription') {
    return null;
  }

  return (
    <FormSection title="Free trial">
      <FormSubSection>
        <FormGroup prop="freeTrialPeriod" class="inline-flex items-center space-x-2">
          <label class="inline-flex items-center space-x-3">
            <Toggle checked={value > 0} onClick={() => setValue(value === 0 ? 7 : 0)} />
            <span>Enable free trial for</span>
          </label>
          <input
            type="number"
            name="freeTrialPeriod"
            class="inline-ruz-input w-16 appearance-none"
            placeholder="#"
            disabled={value === 0}
            // Display placeholder when value is 0
            value={value || ''}
            onBlur={(e: any) => {
              const newValue = parseInt(e.target.value, 10);
              setValue(newValue || 0);
            }}
          />
          <span>days</span>
        </FormGroup>
      </FormSubSection>
    </FormSection>
  );
}

export function Provider({
  paymentType,
  supportsStripe,
  checkStripe,
}: {
  paymentType: PaymentType;
  supportsStripe: boolean;
  checkStripe?: boolean;
}) {
  if (paymentType === 'free' || !supportsStripe) {
    return null;
  }

  return (
    <FormSection title="Payment processing">
      <FormSubSection>
        <FormGroup prop="providers">
          <label>
            <Toggle checked={isNullish(checkStripe) || checkStripe} name="allowStripe" />
            <span class="ml-3">Stripe</span>
          </label>
        </FormGroup>
      </FormSubSection>
    </FormSection>
  );
}

function nameFromUIType(uiType: UIPaymentType) {
  if (uiType === 'free') {
    return 'Free';
  }
  if (uiType === 'pwyc') {
    return 'Pay What You Can';
  }
  if (uiType === 'paid') {
    return 'Single Payment';
  }
  if (uiType === 'subscription') {
    return 'Subscription';
  }
  if (uiType === 'paymentplan') {
    return 'Payment Plan';
  }
}

export function PricePointName({
  name,
  paymentType,
  uiType,
}: {
  name?: string;
  paymentType: PaymentType;
  uiType?: UIPaymentType;
}) {
  return (
    <FormSection title="Name">
      <FormSubSection>
        <FormGroup prop="name">
          <input
            class="inline-ruz-input"
            type="text"
            name="name"
            value={name}
            maxLength={50}
            placeholder={nameFromUIType(uiType || paymentType)}
          />
        </FormGroup>
      </FormSubSection>
    </FormSection>
  );
}

function BtnChoice({
  children,
  isSelected,
  ...props
}: { isSelected: boolean } & h.JSX.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      type="button"
      class={`px-4 py-2 inline-flex items-center space-x-2 border rounded mb-2 mr-3 focus:outline-hidden hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-500  focus:ring-1 focus:ring-indigo-500 ${
        isSelected
          ? 'border-indigo-300 bg-indigo-50 text-indigo-700 focus:bg-indigo-100'
          : 'border-gray-100 bg-gray-50 text-gray-600'
      }`}
    >
      {children}
    </button>
  );
}

function ChoosePaymentType({
  uiType,
  setUIType,
  supportsStripe,
  courseId,
}: {
  uiType: UIPaymentType;
  setUIType(t: UIPaymentType): void;
  supportsStripe: boolean;
  courseId?: string;
}) {
  return (
    <FormSection title="Payment type">
      <FormSubSection>
        <FormGroup prop="paymentType" class="flex flex-wrap whitespace-nowrap">
          <input type="hidden" name="paymentType" value={uiType} />
          <BtnChoice isSelected={uiType === 'free'} onClick={() => setUIType('free')}>
            <IcoGift />
            <span>Free</span>
          </BtnChoice>
          {supportsStripe && (
            <>
              <BtnChoice isSelected={uiType === 'paid'} onClick={() => setUIType('paid')}>
                <IcoDollar />
                <span>Single Payment</span>
              </BtnChoice>
              <BtnChoice isSelected={uiType === 'pwyc'} onClick={() => setUIType('pwyc')}>
                <IcoHeart />
                <span>Pay What You Can</span>
              </BtnChoice>
              <BtnChoice
                isSelected={uiType === 'paymentplan'}
                onClick={() => setUIType('paymentplan')}
              >
                <IcoCalendar />
                <span>Payment Plan</span>
              </BtnChoice>
              <BtnChoice
                isSelected={uiType === 'subscription'}
                onClick={() => setUIType('subscription')}
              >
                <IcoRefresh />
                <span>Subscription</span>
              </BtnChoice>
            </>
          )}
          {!supportsStripe && (
            <p class="inline-block ml-2">
              <BtnStripeConnect class="text-indigo-600" courseId={courseId}>
                Configure Stripe
              </BtnStripeConnect>{' '}
              to accept payments.
            </p>
          )}
        </FormGroup>
      </FormSubSection>
    </FormSection>
  );
}

export function RedirectUrlField(props: { value?: string }) {
  return (
    <label class="flex flex-col gap-1">
      <span class="font-medium">Post-purchase redirect URL</span>
      <input
        type="text"
        name="redirectUrl"
        placeholder="Leave blank for default (course profile page)"
        value={props.value || ''}
        class="ruz-input"
      />
      <span class="text-xs text-gray-500">
        Where to send the student after purchase. Can be a path like /community or a full URL.
      </span>
    </label>
  );
}

export function ExpandableLimitsFields({ price }: { price?: PriceRow }) {
  const hasLimits = price?.maxPurchases || price?.expiresOn || price?.availableOn;
  const [showLimits, setShowLimits] = useState(!!hasLimits);
  if (!showLimits) {
    return (
      <span>
        <Button
          class="flex items-center gap-2 justify-between border rounded-lg p-2 px-4"
          onClick={() => setShowLimits(true)}
        >
          <span>Configure signup limits (optional)</span> <IcoChevronDown />
        </Button>
      </span>
    );
  }
  return <SignupLimitsFormFields itemName="price point" item={price} />;
}
